/**
 * src/app/venue/VenueBottomNav.tsx — 2704va
 * Two roles, minimal nav.
 * Picker: Inflow + Pick
 * Manager: Shortlist + Library + Settings (opens SortsManagerModal)
 */

import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Layers, CloudUpload, LayoutGrid, BookImage, Settings } from 'lucide-react'
import { getStoredRole, clearStoredRole } from '@/lib/venue-role'
import { SortsManagerModal } from '@/components/ui/SortsManagerModal'

interface Props {
  venueSlug:        string
  venueId?:         string
  unactionedCount:  number
  pendingApprovals: number
  queuedCount:      number
}

const CX_BASE = 'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-0.5 relative min-h-[48px]'
const CX_ACTIVE   = 'bg-room-accent/80 text-white shadow-lg'
const CX_INACTIVE = 'text-room-muted hover:text-white'
const tabClass = (a: boolean) => `${CX_BASE} ${a ? CX_ACTIVE : CX_INACTIVE}`

function Badge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center bg-amber-500 text-black">
      {count > 9 ? '9+' : count}
    </span>
  )
}

export function VenueBottomNav({ venueSlug, venueId, unactionedCount }: Props) {
  const base     = `/venue/${venueSlug}`
  const navigate = useNavigate()
  const role     = getStoredRole(venueSlug)
  const [showSorts, setShowSorts] = useState(false)

  function handleSettings() {
    if (role === 'manager' && venueId) {
      setShowSorts(true)
    } else {
      clearStoredRole(venueSlug)
      navigate(`/venue/${venueSlug}/enter`, { replace: true })
    }
  }

  return (
    <>
      <nav className="fixed bottom-0 inset-x-0 z-20">
        <div className="max-w-lg mx-auto px-4 pb-4">
          <div className="bg-black/80 border border-room-border rounded-2xl p-1.5 flex gap-1">

            {role === 'picker' && <>
              <NavLink to={`${base}/inflow`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><CloudUpload size={18} strokeWidth={isActive ? 2.5 : 1.8} />Inflow</>)}
              </NavLink>
              <NavLink to={`${base}/picker`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<>
                  <Layers size={18} strokeWidth={isActive ? 2.5 : 1.8} />
                  Pick
                  <Badge count={unactionedCount} />
                </>)}
              </NavLink>
            </>}

            {role === 'manager' && <>
              <NavLink to={`${base}/shortlist`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><LayoutGrid size={18} strokeWidth={isActive ? 2.5 : 1.8} />Shortlist</>)}
              </NavLink>
              <NavLink to={`${base}/library`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><BookImage size={18} strokeWidth={isActive ? 2.5 : 1.8} />Library</>)}
              </NavLink>
            </>}

            {!role && <>
              <NavLink to={`${base}/inflow`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><CloudUpload size={18} strokeWidth={isActive ? 2.5 : 1.8} />Inflow</>)}
              </NavLink>
              <NavLink to={`${base}/picker`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><Layers size={18} strokeWidth={isActive ? 2.5 : 1.8} />Pick</>)}
              </NavLink>
              <NavLink to={`${base}/shortlist`} className={({ isActive }) => tabClass(isActive)}>
                {({ isActive }) => (<><LayoutGrid size={18} strokeWidth={isActive ? 2.5 : 1.8} />Shortlist</>)}
              </NavLink>
            </>}

            <button type="button" onClick={handleSettings}
              className={`${CX_BASE} ${CX_INACTIVE} shrink-0 px-2 flex-none`}
              title={role === 'manager' ? 'Manage sorts' : 'Switch role'}>
              <Settings size={16} strokeWidth={1.8} />
              <span style={{ fontSize: '9px' }}>{role === 'manager' ? 'Sorts' : 'Role'}</span>
            </button>

          </div>
        </div>
      </nav>

      {showSorts && venueId && (
        <SortsManagerModal venueId={venueId} onClose={() => setShowSorts(false)} />
      )}
    </>
  )
}
